import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import type { LifecycleState } from '../lib/billing';
import { tasksAPI } from '../services/api';
import { AuthContext } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { apiErrorText, creditsExhaustedCard, makeCreditsExhaustedError } from '../lib/billing';
import { isTerminalTaskStatus, pickSettlementFacts } from '../utils/scaffoldCover';
import { applyChatMessageComplete } from '../utils/chatOrder';
import {
  ITask as ITaskType,
  IChatMessage,
  ICommandLog,
  ITerminalOutput,
  ExecutionStatus,
  MessageType,
  SubTask,
  CommandExecution,
  PreviewSelectionContext,
  PreviewPageContext,
} from '../types/terminal';

// Create local interface for backward compatibility with original ITask
export interface ITask {
  id: string;
  title: string;
  description: string;
  status: string;
  agent?: 'claude' | 'cursor';
  branch: string;
  owner: string;
  workingBranch?: string;
  targetBranch?: string;
  prUrl?: string;
  prNumber?: number;
  createdAt?: string;
  // Last activity. Present on every task payload (Task has timestamps:true and
  // the list endpoint does not project fields away) but previously untyped, so
  // the sidebar fell back to createdAt and showed a task's AGE instead of how
  // recently it ran. A 3-month-old task re-run today read "3mo".
  updatedAt?: string;
  progress?: number;
  /** Server-computed on GET /tasks/:id: some turn on this task has COMPLETED
   *  (not stopped, not failed). Reload-surviving evidence that a scratch
   *  project's first build produced its page — the scaffolding cover reads it
   *  (utils/scaffoldCover.ts). Absent on list payloads. */
  hasCompletedTurn?: boolean;
  /** GET /tasks/:id, with hasCompletedTurn (utils/interruptedBuild.ts): why the
   *  app exists — a completed turn, or the presentability check saw it after an
   *  interrupted one. */
  appExistsVia?: 'completed' | 'presentable' | null;
  /** How the latest turn ended when it did not complete: credits_exhausted,
   *  user_stopped, failed. */
  interruptedOutcome?: string | null;
  /** Whether the server's check of that turn's preview is in. */
  presentability?: 'pending' | 'decided' | 'undecided' | null;
  /** Optimistic-concurrency version (T7). Echoed as expectedRev on rename so
   *  the server rejects a stale write (409) instead of clobbering a teammate. */
  rev?: number;
  /** Set when the user pins this task (sidebar). Truthy = pinned. Persisted
   *  server-side via PUT /tasks/:id { pinned }. */
  pinnedAt?: string | null;
  // v2 plan mode (PLAN_SESSION_V2): if set, this task was spawned from a
  // PlanSession Approve. The task detail page reads this to render a "back
  // to plan session" link so the user can return to the planning thread
  // and approve more plans / spawn sibling tasks.
  planSession?: string | null;
  images?: {
    id: string;
    url: string;
    name: string;
    size: number;
    type: string;
    uploadedAt: Date;
  }[];
  project: {
    id?: string;
    name: string;
    githubRepoName?: string;
    githubOwner?: string;
    baseUrl?: string;
    // Set on Project at create time. 'scratch' = created via /api/scratch
    // (the "Create new app" composer flow); 'imported' = github connect.
    // TaskDetailPage gates the hideUntilFirstEdit preview overlay on this.
    source?: 'imported' | 'scratch';
    buildKind?: 'landing' | 'fullstack' | 'mobile' | 'video' | null;
    templateId?: string | null;
    treeProfile?: 'fdev-starter' | 'byo' | null;
    // Pricing lifecycle (T6/T11): paused / archived with its reason; the
    // task page's banner reads it (design decision 7).
    lifecycle?: { state: LifecycleState; pauseReason?: string | null; archiveReason?: string | null; pausedAt?: string | null; archivedAt?: string | null } | null;
    // Set when the project's machine first served successfully. Distinguishes
    // a first build (still scaffolding) from a reboot of a finished app —
    // drives the scaffold cover and the boot panel's title.
    firstBootCompletedAt?: string | null;
    // The 24-hour template trial's end (server Project.templateTrial): the
    // countdown banner, and no code views while the org has no plan.
    templateTrial?: { endsAt?: string | null } | null;
    settings?: {
      deployment?: {
        type?: 'none' | 'amplify' | 'github_actions';
        workflowFile?: string;
        timeoutMinutes?: number;
      };
    };
  };
  // Set by the server as the agent writes/edits files in the workspace.
  // TaskDetailPage uses `.length === 0` as the "agent hasn't produced
  // anything yet" gate so the scratch preview overlay drops as soon as the
  // first edit lands.
  filesChanged?: Array<{ path: string; action?: string; linesAdded?: number; linesDeleted?: number }>;
}



interface ITaskContext {
  tasks: ITask[];
  selectedTask: ITask | null;
  chatMessages: IChatMessage[];
  commandLogs: ICommandLog[];
  terminalOutput: ITerminalOutput[];
  executionStatus: ExecutionStatus;
  /** Which mode the IN-FLIGHT turn was dispatched in, or null when nothing is
   *  running. executionStatus alone can't tell a plan turn from a build turn
   *  — both read 'running' — so a saved-plan card was showing isBuilding
   *  spinner + "Building…" during a plan turn while the preview cover said
   *  "nothing is being built yet" in the same breath (2026-09-21 fix). Set at
   *  sendChatMessage's dispatch point; cleared on terminal WS status and on
   *  task-selection clear. */
  activeTurnMode: 'build' | 'plan' | null;
  isExecutionBlocked: boolean;
  loading: boolean;
  error: string | null;
  executions: CommandExecution[];
  setSelectedTask: React.Dispatch<React.SetStateAction<ITask | null>>;
  
  // Task methods
  fetchTasks: (params?: any) => Promise<void>;
  createTask: (data: any) => Promise<ITask>;
  selectTask: (taskId: string | null) => Promise<ITask | null>;
  refreshTaskFromDB: (taskId: string) => Promise<void>;
  updateTask: (taskId: string, updates: Partial<ITask>) => Promise<ITask>;
  // Clears the selection only when the deleted task is still the selected
  // one AT RESOLUTION TIME (ref-checked, never a stale closure).
  deleteTask: (taskId: string) => Promise<void>;

  // Chat methods
  // Resolves to the dispatched turn's chat message id (or null if the server
  // didn't echo it back) so callers can correlate this turn's later terminal
  // command events — used by the plan-card "optimistic Built" revert.
  sendChatMessage: (message: string, type?: MessageType, images?: any[], agent?: 'claude' | 'cursor', previewContext?: PreviewPageContext | null, refs?: { projectId: string; name: string }[], attachments?: any[], mode?: 'build' | 'plan', opts?: { explicitBuild?: boolean; planCardId?: string | null }) => Promise<string | null>;
  /** Cross-task plan building: list the project's saved plans (newest first)
   *  for the composer "+" → "Saved plans" picker. */
  listProjectPlans: () => Promise<Array<{ id: string; name: string; task: string | null; createdAt: string }>>;
  /** Build a plan saved in another task into the current task. No re-approval —
   *  a saved plan doc is already approved. Resolves to the build's message id. */
  buildFromSavedPlan: (planArtifactId: string) => Promise<string | null>;
  fetchChatHistory: (taskId: string, task: ITask) => Promise<void>;
  /** Push a client-only optimistic message into the chat (e.g., while
   *  waiting for preview to boot). Gets replaced by the real server message
   *  when it arrives via websocket or the real send completes. */
  pushOptimisticMessage: (msg: IChatMessage) => void;
  /** Remove a previously-pushed optimistic message by id. */
  removeOptimisticMessage: (id: string) => void;
  
  // Command methods
  executeCommand: (command: string) => Promise<void>;
  cancelExecution: () => Promise<void>;
  fetchCommandLogs: (taskId: string) => Promise<void>;
  
  // Terminal methods
  fetchTerminalOutput: (taskId: string) => Promise<void>;
  fetchExecutions: (taskId: string) => Promise<void>;
  
  // Utility methods
  clearError: () => void;
  setExecutionStatus: React.Dispatch<React.SetStateAction<ExecutionStatus>>;
  /** Exposed for the same reason as setExecutionStatus: handleApprovePlan and
   *  buildFromSavedPlan (TaskDetailPage) dispatch real BUILD turns without
   *  going through sendChatMessage, so they have to set activeTurnMode
   *  themselves, right next to their own optimistic setExecutionStatus('running')
   *  call — otherwise the cover reads "Planning…" over an approved build
   *  (review fix, 2026-09-21). */
  setActiveTurnMode: React.Dispatch<React.SetStateAction<'build' | 'plan' | null>>;
}

export const TaskContext = createContext<ITaskContext | undefined>(undefined);

export const useTask = () => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTask must be used within a TaskProvider');
  }
  return context;
};

export const TaskProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<ITask[]>([]);
  const [selectedTask, setSelectedTask] = useState<ITask | null>(null);
  // Always-current snapshot of tasks for callbacks that must read the latest
  // `rev` without re-binding (T7 optimistic-concurrency on rename).
  const tasksRef = useRef<ITask[]>([]);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);
  // Same pattern for the selection: async callbacks (deleteTask) must compare
  // against the selection AS OF their resolution, never their creation — a
  // stale closure here is what wiped a freshly-loaded task and dead-ended the
  // page on "Task Not Found" (2026-08-27, import new-task handoff).
  const selectedTaskRef = useRef<ITask | null>(null);
  useEffect(() => { selectedTaskRef.current = selectedTask; }, [selectedTask]);
  const [chatMessages, setChatMessages] = useState<IChatMessage[]>([]);
  const [commandLogs, setCommandLogs] = useState<ICommandLog[]>([]);
  const [terminalOutput, setTerminalOutput] = useState<ITerminalOutput[]>([]);
  const [executions, setExecutions] = useState<CommandExecution[]>([]);
  const [executionStatus, setExecutionStatus] = useState<ExecutionStatus>('pending');
  // See ITaskContext.activeTurnMode — narrows 'running' to build vs plan so
  // isBuilding can be honest (2026-09-21 fix).
  const [activeTurnMode, setActiveTurnMode] = useState<'build' | 'plan' | null>(null);
  const [isExecutionBlocked, setIsExecutionBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useContext(AuthContext);
  const { socket, isConnected } = useWebSocket();

  const fetchTasks = useCallback(async (params?: any) => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const response = await tasksAPI.getAll(params);
      // A 200 whose body is not the API's (a proxy or captive-portal page, an
      // extension) left `tasks` undefined and crashed the Dashboard on
      // tasks.length (Sentry NODE-EXPRESS-14).
      if (!Array.isArray(response.data?.tasks)) {
        setError('Failed to fetch tasks');
        return;
      }
      setTasks(response.data.tasks);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const selectTask = useCallback(async (taskId: string | null): Promise<ITask | null> => {
    if (!taskId) {
      setSelectedTask(null);
      setChatMessages([]);
      setCommandLogs([]);
      setTerminalOutput([]);
      setExecutions([]);
      setExecutionStatus('pending');
      setActiveTurnMode(null);
      setIsExecutionBlocked(false);
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('TaskContext: Selecting task:', taskId);
      
      // Clear previous data when selecting a new task
      setChatMessages([]);
      setCommandLogs([]);
      setTerminalOutput([]);
      // Switching tasks must drop the previous task's in-flight mode too —
      // otherwise task A's running BUILD leaks into task B and B's plan card
      // reads "Building…" off a turn that isn't B's (2026-09-21 fix).
      setActiveTurnMode(null);
      
      // Fetch task details
      const taskResponse = await tasksAPI.getById(taskId);
      const { task } = taskResponse.data; // API returns { task, logs }
      
      console.log('TaskContext: Task loaded:', task);
      setSelectedTask(task);
      
      // Set initial execution status from the task object
      const initialStatus = task.status || 'pending';
      console.log('TaskContext: Setting initial execution status:', initialStatus);
      setExecutionStatus(initialStatus as ExecutionStatus);
      // Block execution if task is currently running
      setIsExecutionBlocked(initialStatus === 'running');
      return task;

    } catch (err: any) {
      console.error('TaskContext: Failed to select task:', err);
      setError(err.response?.data?.error || 'Failed to fetch task');
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Separate function for refreshing task data without overwriting real-time updates
  const refreshTaskFromDB = useCallback(async (taskId: string) => {
    if (!taskId || !token) return;
    
    try {
      console.log('TaskContext: Refreshing task from database:', taskId);
      const taskResponse = await tasksAPI.getById(taskId);
      const { task } = taskResponse.data;
      
      // Only update if the task is different or if we don't have real-time updates
      // (This preserves WebSocket updates)
      setSelectedTask(prev => {
        if (!prev || prev.id !== task.id) {
          console.log('TaskContext: Updating task from DB (new task)');
          return task;
        }
        // Keep the real-time status if it's more recent
        console.log('TaskContext: Keeping existing task to preserve real-time updates');
        return prev;
      });
    } catch (err: any) {
      console.error('TaskContext: Failed to refresh task:', err);
    }
  }, [token]);

  const sendChatMessage = useCallback(async (message: string, type: MessageType = 'user', images: any[] = [], agent?: 'claude' | 'cursor', previewContext?: PreviewPageContext | null, refs?: { projectId: string; name: string }[], attachments: any[] = [], mode: 'build' | 'plan' = 'build', opts?: { explicitBuild?: boolean; planCardId?: string | null }): Promise<string | null> => {
    if (!selectedTask || !token) return null;

    try {
      setError(null);
      // Idempotency key (T6): mint one fresh id per send. If the request is
      // retried (flaky network, double-click, socket reconnect), the same id
      // lets the server collapse the duplicate so the message can't double-post
      // and the agent can't double-run. Fall back to a random string where
      // crypto.randomUUID is unavailable (older browsers).
      const clientMsgId =
        (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
          ? crypto.randomUUID()
          : `cm_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      // Record which mode THIS turn is dispatching in — executionStatus alone
      // reads 'running' for both build and plan, which is what let a plan
      // turn's saved-plan card show isBuilding's spinner (2026-09-21 fix).
      setActiveTurnMode(mode);
      // Create chat message via API. attachments[] carries doc uploads
      // (PDF/DOCX/XLSX/TXT/MD with extractedText) — previously dropped on
      // command-mode follow-ups, so the agent never saw the spec. /qa
      // BUG-B fix wires them through the task-scoped SDK send shape.
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/tasks/${selectedTask.id}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: message,
          messageType: type,
          // Plan mode messages always trigger an SDK turn — the server's
          // chat-send route only runs the worker on the `isCommand` branch,
          // so we mark plan messages as commands even when `type` is 'user'.
          isCommand: type === 'command' || mode === 'plan',
          commandText:
            type === 'command' || mode === 'plan' ? message : undefined,
          images: images,
          attachments: attachments || [],
          agent: agent,
          previewContext: previewContext || null,
          refs: refs || [],
          // Build vs Plan dispatches through the same endpoint; the server
          // forwards `mode` to the SDK (permissionMode:'plan' for plan).
          // Plan turns surface as messageType:'plan' chat rows via socket.
          mode: mode,
          // Explicit build dispatches (saved-plan card "Build this", the
          // approval-flow build branch) must reach the server even while the
          // task is still parked at status:'planning' — the 2026-08-22
          // plan-revision guard otherwise coerces every non-plan message back
          // to 'plan' on a planning task, which silently no-ops a build the
          // user explicitly approved (2026-09-22).
          buildIntent: opts?.explicitBuild ? 'explicit' : undefined,
          // Which plan card this build came from, so the server can settle it
          // to 'built' in the same request (routes/taskChat.js). Without it the
          // server falls back to the newest saved card, which is right in the
          // single-plan case but guesses when a task holds several — name it
          // when we know it (2026-09-22).
          planCardId: opts?.planCardId || undefined,
          // Idempotency key — server dedups retries carrying the same id (T6).
          clientMsgId,
        }),
      });

      if (!response.ok) {
        // Surface the server's actual reason (validation message, 400 body,
        // etc.) instead of a generic string — callers show this to the user.
        let msg = 'Failed to send message';
        let body: any = null;
        try {
          body = await response.json();
          // The pricing gate's 402 carries error as {type, message}.
          const e = body?.error;
          msg = (typeof e === 'string' ? e : e?.message) || body?.errors?.[0]?.msg || msg;
        } catch (_parseErr) {
          // Non-JSON error body — fall back to the generic message.
        }
        // Out of building credit (pricing gate design D8): nothing was sent
        // or spent. A typed error, so the page renders the pause card and the
        // composer keeps the user's text (ChatComponent restores on throw).
        if (response.status === 402 && body?.code === 'credits_exhausted') {
          throw makeCreditsExhaustedError(body.card || null);
        }
        throw new Error(msg);
      }

      const result = await response.json();
      
      if (result.success && result.chatMessage) {
        setChatMessages(prev => {
          // Normalize id field
          const newMsg = {
            ...result.chatMessage,
            id: result.chatMessage.id
          };
          // Remove any message with the same id
          const filtered = prev.filter(m => m.id !== newMsg.id);
          return [...filtered, newMsg].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        });
        return result.chatMessage.id ?? null;
      } else {
        // If for some reason the message isn't returned, fall back to a refetch
        const chatResponse = await tasksAPI.getChatHistory(selectedTask.id);
        setChatMessages(chatResponse.data.messages);
        return null;
      }
     } catch (err: any) {
       // The dispatch never made it (or the server rejected it) — no turn is
       // actually running, so the optimistic setActiveTurnMode(mode) above
       // has to unwind too, or a failed send leaves activeTurnMode stuck on
       // 'build'/'plan' with nothing in flight to ever clear it (the WS
       // terminal-status clear never fires for a turn that never started).
       // Callers re-throw from here and restore their own optimistic state
       // (composer text, executionStatus) in their own catch blocks; this is
       // the one place that owns activeTurnMode's optimism (review fix,
       // 2026-09-21).
       setActiveTurnMode(null);
       // A pause card is not an error banner (the card says it all).
       if (err?.code !== 'credits_exhausted') setError(err.response?.data?.error || 'Failed to send message');
       throw err;
     }
   }, [selectedTask, token]);

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const listProjectPlans = useCallback(async (): Promise<Array<{ id: string; name: string; task: string | null; createdAt: string }>> => {
    if (!selectedTask || !token) return [];
    try {
      const res = await fetch(`${apiBase}/api/tasks/${selectedTask.id}/plans`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data.plans) ? data.plans : [];
    } catch (_e) {
      return [];
    }
  }, [selectedTask, token, apiBase]);

  const buildFromSavedPlan = useCallback(async (planArtifactId: string): Promise<string | null> => {
    if (!selectedTask || !token) return null;
    try {
      setError(null);
      const res = await fetch(`${apiBase}/api/tasks/${selectedTask.id}/chat/build-from-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ planArtifactId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // Out of credit (402): the typed error the page turns into a pause
        // card. Its `error` is an object, never a toast string.
        if (res.status === 402 && data && data.code === 'credits_exhausted') {
          throw makeCreditsExhaustedError(data.card || null);
        }
        throw new Error(apiErrorText({ response: { data } }, 'Failed to build from saved plan'));
      }
      const data = await res.json();
      return data.messageId ?? null;
    } catch (err: any) {
      // The pause card is the whole message for a credit refusal.
      if (!creditsExhaustedCard(err).hit) setError(err.message || 'Failed to build from saved plan');
      throw err;
    }
  }, [selectedTask, token, apiBase]);

  const fetchChatHistory = useCallback(async (taskId: string, task: ITask) => {
    if (!token) return;
    
    try {
      setError(null);
      const response = await tasksAPI.getChatHistory(taskId);
      const messages = response.data.messages || [];

      // NOTE: we used to inject a synthetic "📎 Task Images" system row from the
      // task-level `task.images` here. That duplicated the image — task creation
      // persists the initial image to BOTH Task.images AND the first command
      // message's images, and the command message now renders its own images in
      // the chat bubble. The synthetic row was a legacy artifact from when images
      // were task-level only (commit 5c66bb3) and is redundant now. Removed so
      // the image shows once, on the user's message. `task` is kept in the
      // signature for callers/back-compat.
      void task;
      setChatMessages(messages);
    } catch (err: any) {
      console.error('TaskContext: Failed to fetch chat history:', err);
      setError(err.response?.data?.error || 'Failed to fetch chat history');
    }
  }, [token]);

  const executeCommand = useCallback(async (command: string) => {
    if (!selectedTask || !token) return;
    
    try {
      setError(null);
      setExecutionStatus('running');
      setIsExecutionBlocked(true);
      
      // This would be implemented with the new command API
      console.log('Executing command:', command);
      // const response = await commandAPI.execute(selectedTask.id, { command });
      // Handle response...
      
    } catch (err: any) {
      setExecutionStatus('failed');
      setIsExecutionBlocked(false);
      setError(err.response?.data?.error || 'Failed to execute command');
      throw err;
    }
  }, [selectedTask, token]);

  const cancelExecution = useCallback(async () => {
    if (!selectedTask || !token) return;
    
    try {
      setError(null);
      // This would be implemented with the new command API
      console.log('Cancelling execution for task:', selectedTask.id);
      // await commandAPI.cancel(commandId);
      setExecutionStatus('cancelled');
      setIsExecutionBlocked(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel execution');
      throw err;
    }
  }, [selectedTask, token]);

  const fetchCommandLogs = useCallback(async (taskId: string) => {
    if (!token) return;
    
    try {
      setError(null);
      // This would be implemented with the new command API
      // console.log('Fetching command logs for task:', taskId);
      const response = await tasksAPI.getCommandLogs(taskId);
      // Same non-API-body guard as fetchTasks.
      if (!Array.isArray(response.data?.logs)) {
        setError('Failed to fetch command logs');
        return;
      }
      setCommandLogs(response.data.logs);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch command logs');
    }
  }, [token]);

  const fetchTerminalOutput = useCallback(async (taskId: string) => {
    if (!token) return;
    
    try {
      setError(null);
      console.log('TaskContext: Fetching terminal output for task:', taskId);
      const response = await tasksAPI.getTerminalOutput(taskId);
      console.log('TaskContext: Terminal output response:', response.data);
      if (response.data && response.data.output) {
        console.log('TaskContext: Setting terminal output with', response.data.output.length, 'entries');
        setTerminalOutput(response.data.output);
      } else {
        console.log('TaskContext: No terminal output in response');
        setTerminalOutput([]);
      }
    } catch (err: any) {
      console.error('TaskContext: Failed to fetch terminal output:', err);
      setError(err.response?.data?.error || 'Failed to fetch terminal output');
    }
  }, [token]);

  const fetchExecutions = useCallback(async (taskId: string) => {
    if (!token) return;
    
    try {
      setError(null);
      const response = await tasksAPI.getExecutions(taskId);
      if (response.data && response.data.data) {
        setExecutions(response.data.data);
      }
    } catch (err: any) {
      console.error('TaskContext: Failed to fetch executions:', err);
    }
  }, [token]);

  const createTask = useCallback(async (data: any) => {
    if (!token) throw new Error('Authentication token not found');
    setLoading(true);
    try {
      const response = await tasksAPI.create(data);
      // Refresh the list best-effort. If this fails, the create itself still
      // succeeded server-side — swallow the refresh error so callers never
      // see "Failed to create" and retry, which would duplicate the task.
      fetchTasks({ project: data.projectId }).catch((refreshErr) => {
        console.warn('TaskContext: post-create task list refresh failed:', refreshErr?.message);
      });
      return response.data.task as ITask;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create task');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token, fetchTasks]);

  const updateTask = useCallback(async (taskId: string, updates: Partial<ITask>) => {
    if (!token) throw new Error("Authentication token not found");
    // Optimistic concurrency (T7): when renaming, echo the rev we last saw so
    // the server rejects a stale write (409) rather than silently clobbering a
    // teammate's concurrent rename. Only for title changes — pin toggles are
    // idempotent and don't need the guard.
    let body: any = updates;
    if (typeof updates.title === 'string') {
      const current = tasksRef.current.find(t => t.id === taskId);
      if (current && typeof current.rev === 'number') {
        body = { ...updates, expectedRev: current.rev };
      }
    }
    let response;
    try {
      response = await tasksAPI.update(taskId, body);
    } catch (err: any) {
      // 409 = someone else renamed this first. Adopt the server's current state
      // (no silent overwrite) and surface a clear, non-technical message.
      if (err?.response?.status === 409 && err.response.data?.current) {
        const serverTask = err.response.data.current as ITask;
        setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, ...serverTask } : t)));
        setError('Someone else just renamed this. We kept their change — try again if needed.');
        return serverTask;
      }
      throw err;
    }
    const updated = response.data.task;
    // PUT /tasks/:id returns the task with `project` UNPOPULATED (a raw id
    // string), unlike the list endpoint which returns { id, name, ... }. Merge
    // the changed fields onto the existing row and KEEP the populated project,
    // so project-scoped filters (t.project?.id) don't drop the task (which made
    // a pinned/renamed task vanish from the sidebar until a refetch).
    setTasks(prev => prev.map(task => {
      if (task.id !== taskId) return task;
      return {
        ...task,
        ...updated,
        project: updated?.project && typeof updated.project === 'object'
          ? updated.project
          : task.project,
      };
    }));
    return updated;
  }, [token]);

  const deleteTask = useCallback(async (taskId: string) => {
    if (!token) throw new Error("Authentication token not found");
    await tasksAPI.delete(taskId);
    setTasks(prev => prev.filter(task => task.id !== taskId));
    // The "is the deleted task selected" check must be made AT RESOLUTION
    // TIME (via ref), never against this callback's closure. The old closure
    // check caused a production dead-end (2026-08-27): a background delete
    // of the import flow's old anchor task resolved AFTER the user had
    // navigated to and loaded a new task — the stale closure still said the
    // anchor was selected, so selectTask(null) wiped the freshly-loaded
    // task and the page sat on "Task Not Found" until a manual refresh.
    // (That anchor flow is retired; the resolution-time check stays so no
    // future background delete can be bitten the same way.)
    if (selectedTaskRef.current?.id === taskId) {
      selectTask(null);
    }
  }, [token, selectTask]);

  const retryTask = useCallback(async (id: string) => {
    if (!token) return;
    try {
      setError(null);
      const response = await tasksAPI.retry(id);
      const updatedTask = response.data.task;
      setTasks(prev => prev.map(t => t.id === id ? updatedTask : t));
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to retry task');
      throw error;
    }
  }, [token]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);



  // Periodic refresh for pending tasks
  useEffect(() => {
    if (selectedTask && selectedTask.status === 'pending') {
      console.log('TaskContext: Setting up periodic refresh for pending task');
      const interval = setInterval(async () => {
        console.log('TaskContext: Refreshing pending task status');
        await refreshTaskFromDB(selectedTask.id);
      }, 5000); // Check every 5 seconds

      return () => {
        clearInterval(interval);
      };
    }
  }, [selectedTask?.id, selectedTask?.status, refreshTaskFromDB]);

  // WebSocket event handlers
  useEffect(() => {
    if (isConnected && selectedTask && selectedTask.id && socket) {
      const handleTerminalOutput = (data: ITerminalOutput) => {
        console.log('TaskContext: Received terminal_output:', data);
        if (data.task === selectedTask?.id) {
          console.log('TaskContext: Adding terminal output to state');
          setTerminalOutput(prev => [...prev, data]);
        }
      };

      const handleChatMessage = (data: any) => {
        // Accept either `task` or `taskId` (different emit sites use each).
        const tid = data.task || data.taskId;
        if (tid !== selectedTask.id) return;
        setChatMessages(prev => {
          // Idempotent append (T6): this event now also fires for the sender's own
          // message (which they inserted optimistically) and can arrive in either
          // order vs the HTTP response, so de-dupe by id and clientMsgId. Without
          // this, the sender would see their message twice.
          const id = data.id || data._id;
          const cmid = data.clientMsgId;
          const dup = prev.some(
            (m: any) =>
              (id && (m.id === id || m._id === id)) ||
              (cmid && m.clientMsgId === cmid)
          );
          if (dup) return prev;
          const normalized = { ...data, id: id || data._id };
          // Client-side superseding for in-flight status rows: when a new
          // status arrives and the latest message is a still-live status, mark
          // the previous one superseded so only the newest "Thinking…"
          // indicator stays live (guards against stacked status rows).
          if (data.messageType === 'status' && prev.length > 0) {
            const last = prev[prev.length - 1];
            if (
              last.messageType === 'status' &&
              !(last.payload as { supersededBy?: string | null } | undefined)?.supersededBy
            ) {
              return [
                ...prev.slice(0, -1),
                { ...last, payload: { ...(last.payload || {}), supersededBy: data.id } },
                normalized,
              ];
            }
          }
          return [...prev, normalized];
        });
      };

      const handleChatMessageStatusChanged = (data: any) => {
        if (data.taskId === selectedTask.id) {
          setChatMessages(prev => prev.map(message => 
            message.id === data.messageId 
              ? { ...message, commandStatus: data.commandStatus, response: data.response || message.response }
              : message
          ));
        }
      };

      // Patch the sidebar list too. Without this the badge is frozen at
      // whatever the initial list fetch returned: a task that ran for 15
      // minutes showed "Starting" the entire time, because only selectedTask
      // and executionStatus were being updated. Same shape as
      // handleTaskTitleUpdated below.
      const patchTaskInList = (taskId: string, status: string, progress?: number) => {
        setTasks(prev => prev.map(t => (
          t.id === taskId
            ? { ...t, status, ...(progress !== undefined ? { progress } : {}) }
            : t
        )));
      };

      const handleTaskStatusChanged = (data: any) => {
        console.log('TaskContext: Received task_status_changed:', data);
        if (data.taskId) patchTaskInList(data.taskId, data.status, data.progress);
        if (data.taskId === selectedTask?.id) {
          console.log('TaskContext: Updating task status and execution status');
          setSelectedTask(prev => {
            if (!prev) return null;
            return {
              ...prev,
              status: data.status,
              progress: data.progress,
              workingBranch: data.workingBranch || prev.workingBranch,
              // A new turn is under way: the previous turn's outcome and its
              // presentability check no longer describe the latest turn. Kept,
              // a later stop would show the OLD outcome ("out of credit" after
              // a plain Stop) until the next read. appExistsVia stays: the
              // app existing is a fact about the task, not the turn.
              ...(isTerminalTaskStatus(data.status) ? {} : { interruptedOutcome: null, presentability: null }),
              // A terminal event can carry the server's settlement facts for
              // the turn it ends (worker.js, ISSUE-005). Taken together with
              // the status in this one update, so a completed-but-unproven
              // first turn never renders as plainly 'completed' first — that
              // frame would drop the cover onto the starter. Absent (older
              // worker, other publish paths): nothing changes.
              ...(isTerminalTaskStatus(data.status) ? pickSettlementFacts(data) : {}),
            };
          });
          // Task model status uses 'in_progress'; map it to the
          // ExecutionStatus 'running' so the UI's working animation kicks in.
          // 'planning' now maps to itself (2026-09-22) instead of falling
          // through to 'pending' — CommandStatus/ExecutionStatus gained the
          // literal, and the reload path (selectTask, below) already assigns
          // the raw task status through this same type, so the live-update
          // mapper was the only place still behind.
          const newExecutionStatus = (data.status === 'running' || data.status === 'in_progress') ? 'running' :
                           data.status === 'completed' ? 'completed' :
                           data.status === 'failed' ? 'failed' :
                           data.status === 'cancelled' ? 'cancelled' :
                           data.status === 'queued' ? 'queued' :
                           data.status === 'planning' ? 'planning' : 'pending';
          setExecutionStatus(newExecutionStatus);
          // The turn that was in flight is over — whichever mode it ran in,
          // nothing is dispatching any more. Was gated on "not
          // running/queued" (2026-09-21), which also cleared on a bare
          // 'pending' — and a stale 'pending' event arriving right after
          // dispatch (a queued status snapshot racing the just-set
          // activeTurnMode) wiped the mode sendChatMessage had JUST set,
          // before the turn's own terminal event ever arrived (observed
          // 2026-09-22). Explicit allowlist instead: only the terminal
          // statuses — including 'planning', the resting state a presented
          // plan parks at — clear activeTurnMode. 'pending' is not one of
          // them; if a turn is genuinely idle, nothing dispatched it and
          // activeTurnMode should already be null from selectTask/dispatch.
          if (
            newExecutionStatus === 'completed' ||
            newExecutionStatus === 'failed' ||
            newExecutionStatus === 'cancelled' ||
            newExecutionStatus === 'planning'
          ) {
            setActiveTurnMode(null);
          }

          // Reconcile on completion (T6): a collaborator who switched away or
          // refreshed mid-stream can miss the final assistant message — its
          // 'chat_message' was emitted while they were off the task, and their
          // reload raced ahead of it being persisted. Without this they'd be
          // stuck showing "Agent is working" with no result until a manual
          // refresh. When the turn reaches a terminal state, refetch the chat
          // so the final messages always appear.
          if (
            (newExecutionStatus === 'completed' ||
              newExecutionStatus === 'failed' ||
              newExecutionStatus === 'cancelled') &&
            selectedTask
          ) {
            fetchChatHistory(selectedTask.id, selectedTask).catch(() => {});
          }

          // Update task in the tasks list as well if it exists
          setTasks(prev => prev.map(task =>
            task.id === data.taskId 
              ? { 
                  ...task, 
                  status: data.status, 
                  progress: data.progress,
                  workingBranch: data.workingBranch || task.workingBranch
                } 
              : task
          ));
        }
      };
      
      const handleGlobalTaskStatusChanged = (data: any) => {
        console.log('TaskContext: Received global_task_status_changed:', data);
        if (data.taskId === selectedTask?.id) {
          handleTaskStatusChanged(data);
          return;
        }
        // A task OTHER than the selected one changed state. Patching the list
        // is this handler's whole purpose — previously it dropped the event on
        // the floor, so sibling tasks in the sidebar never left their initial
        // status until a full refetch.
        if (data.taskId) patchTaskInList(data.taskId, data.status, data.progress);
      };

      const handleCommandStarted = (data: { taskId: string, commandId: string, existingMessageId: string }) => {
        if (data.taskId === selectedTask.id) {
          setExecutions(prev => {
            // Dedup: if an execution with this commandId already exists
            // (e.g. double-registered listener, double-emit from server),
            // keep the existing one instead of pushing a duplicate.
            if (prev.some(exec => exec.commandId === data.commandId)) {
              return prev;
            }
            const newExecution: CommandExecution = {
              commandId: data.commandId,
              messageId: data.existingMessageId,
              phases: { builder: [], verifier: [] },
            };
            return [...prev, newExecution];
          });
        }
      };

      const handleTaskListUpdated = (data: { taskId: string, commandId: string, chatMessageId?: string | null, phase?: 'builder' | 'verifier', tasks: SubTask[] }) => {
        if (data.taskId !== selectedTask.id) return;
        // Default to builder for back-compat with any in-flight events that
        // predate the phase field.
        const phase: 'builder' | 'verifier' = data.phase === 'verifier' ? 'verifier' : 'builder';
        const tagged = data.tasks.map(t => ({ ...t, phase }));
        setExecutions(prev => {
          const matchIdx = prev.findIndex(exec => exec.commandId === data.commandId);
          if (matchIdx >= 0) {
            return prev.map((exec, i) => {
              if (i !== matchIdx) return exec;
              const nextPhases = {
                ...exec.phases,
                [phase]: tagged,
              };
              return { ...exec, phases: nextPhases };
            });
          }
          // Race: task_list_updated landed before command_started. Recover if
          // the server gave us the authoritative chatMessageId.
          if (data.chatMessageId) {
            const recovered: CommandExecution = {
              commandId: data.commandId,
              messageId: data.chatMessageId,
              phases: { builder: [], verifier: [], [phase]: tagged },
            };
            return [...prev, recovered];
          }
          return prev;
        });
      };

      const handleSubtaskStatusUpdated = (data: { taskId: string, commandId: string, subTaskId: string, phase?: 'builder' | 'verifier', status: 'in_progress' | 'completed' | 'failed' }) => {
        if (data.taskId !== selectedTask.id) return;
        const targetPhase: 'builder' | 'verifier' = data.phase === 'verifier' ? 'verifier' : 'builder';
        setExecutions(prev => prev.map(exec => {
          if (exec.commandId !== data.commandId) return exec;
          // Update the matching subtask in the targeted phase only. If the
          // phase field is missing (legacy event), fall back to searching
          // both phases so we don't drop status updates.
          const updatePhase = (phaseTasks: SubTask[]) =>
            phaseTasks.map(t => t.id === data.subTaskId ? { ...t, status: data.status } : t);
          if (data.phase) {
            return {
              ...exec,
              phases: {
                ...exec.phases,
                [targetPhase]: updatePhase(exec.phases[targetPhase]),
              },
            };
          }
          return {
            ...exec,
            phases: {
              builder: updatePhase(exec.phases.builder),
              verifier: updatePhase(exec.phases.verifier),
            },
          };
        }));
      };

      // Coalesced-turn streaming handlers (commit 3 foundation, 2026-05-18).
      // The worker's turnCoalescer (commit 3b will wire it in) emits these:
      //   chat_message_part     — append a new part to an in-flight row
      //   chat_message_complete — flip the row's status to complete/interrupted
      // When commit 3b isn't deployed yet, these events simply don't fire —
      // these handlers are no-ops in that case. Safe to ship now.
      const handleChatMessagePart = (data: {
        taskId: string;
        messageId: string;
        turnId: string;
        partIndex: number;
        part: { type: string; content?: string; name?: string; milestone?: any };
      }) => {
        if (data.taskId !== selectedTask.id) return;
        setChatMessages(prev => prev.map(m => {
          if ((m as any).id !== data.messageId && (m as any)._id !== data.messageId) return m;
          const currentParts = Array.isArray((m as any).parts) ? (m as any).parts : [];
          const nextParts = currentParts.slice();
          nextParts[data.partIndex] = data.part;
          // Keep legacy `message` field roughly in sync so any renderer
          // still reading `m.message` gets the latest text.
          const nextMessage = nextParts
            .filter((p: any) => p && p.type === 'text')
            .map((p: any) => p.content || '')
            .join('');
          return { ...(m as any), parts: nextParts, message: nextMessage } as any;
        }));
      };

      const handleChatMessageComplete = (data: {
        taskId: string;
        messageId: string;
        turnId: string;
        status: 'complete' | 'interrupted';
        endedAt?: string;
      }) => {
        if (data.taskId !== selectedTask.id) return;
        setChatMessages(prev => applyChatMessageComplete(prev as any[], data) as any);
      };

      // Generic payload patch — currently emitted by the approval-card
      // path (worker.js sees `approval_resolved` and updates payload.status
      // on the messageType:'approval' row). Designed so other features can
      // mutate a chat message's payload without inventing a new socket
      // event for each one. Reload-safe because the same payload is
      // already persisted server-side.
      //
      // Two shapes arrive here. The approval/plan writers send
      // { taskId, messageId, payload }. The video rows (server
      // lib/videoChat.js updateCardRow) send the whole row instead,
      // { taskId, message: <wire row> } — the progress line and the card
      // state flips ride that, so without this arm they only showed on reload.
      const handleChatMessageUpdated = (data: {
        taskId: string;
        messageId?: string;
        payload?: any;
        message?: { id?: string; _id?: string; payload?: any; message?: string } | null;
      }) => {
        if (data.taskId !== selectedTask.id) return;
        const row = !data.messageId && data.message && typeof data.message === 'object' ? data.message : null;
        const targetId = data.messageId || (row && (row.id || row._id));
        if (!targetId) return;
        setChatMessages(prev => prev.map(m => {
          if ((m as any).id !== targetId && (m as any)._id !== targetId) return m;
          if (row) {
            return {
              ...(m as any),
              payload: row.payload ?? (m as any).payload,
              ...(typeof row.message === 'string' ? { message: row.message } : {}),
            } as any;
          }
          return { ...(m as any), payload: data.payload } as any;
        }));
      };

      // Async AI title arrived for a task — swap its label in the list. The
      // sidebar reads tasks[].title and cross-fades the change.
      const handleTaskTitleUpdated = (data: { taskId: string; title: string }) => {
        if (!data?.taskId || !data?.title) return;
        setTasks(prev => prev.map(t => (t.id === data.taskId ? { ...t, title: data.title } : t)));
      };

      // Fullstack scratch fast path: the placeholder project name (a
      // heuristic slug, or the user's own typed name) gets swapped for the
      // AI name once it lands, well after the task page is already open.
      // Same dual-patch shape as handleTaskTitleUpdated above — the sidebar
      // list carries its own copy of project.name (tasks.js's list populate
      // already includes it), so both need the update or they drift.
      const handleProjectRenamed = (data: { projectId: string; name: string }) => {
        if (!data?.projectId || !data?.name) return;
        setSelectedTask(prev => {
          if (!prev || !prev.project || String(prev.project.id) !== data.projectId) return prev;
          return { ...prev, project: { ...prev.project, name: data.name } };
        });
        setTasks(prev => prev.map(t => (
          t.project && String(t.project.id) === data.projectId
            ? { ...t, project: { ...t.project, name: data.name } }
            : t
        )));
      };

      // Fullstack scratch fast path: the repo either landed (status:'ready')
      // or the background bootstrap died (status:'failed', already reflected
      // in the task's own status via the server's Task.findByIdAndUpdate —
      // this only needs to flip the provisioning cover on the task page).
      const handleProjectBootstrap = (data: {
        projectId: string;
        taskId: string;
        status: 'ready' | 'failed';
        error?: string;
      }) => {
        if (!data?.projectId) return;
        setSelectedTask(prev => {
          if (!prev || !prev.project || String(prev.project.id) !== data.projectId) return prev;
          return {
            ...prev,
            project: {
              ...prev.project,
              bootstrapStatus: data.status,
              bootstrapError: data.status === 'failed' ? (data.error || null) : null,
            },
          };
        });
      };

      socket.on('terminal_output', handleTerminalOutput);
      socket.on('task_title_updated', handleTaskTitleUpdated);
      socket.on('project_renamed', handleProjectRenamed);
      socket.on('project_bootstrap', handleProjectBootstrap);
      socket.on('chat_message', handleChatMessage);
      socket.on('chat_message_part', handleChatMessagePart);
      socket.on('chat_message_complete', handleChatMessageComplete);
      socket.on('chat_message_updated', handleChatMessageUpdated);
      socket.on('chat_message_status_changed', handleChatMessageStatusChanged);
      socket.on('task_status_changed', handleTaskStatusChanged);
      socket.on('global_task_status_changed', handleGlobalTaskStatusChanged);
      socket.on('command_started', handleCommandStarted);
      socket.on('task_list_updated', handleTaskListUpdated);
      socket.on('subtask_status_updated', handleSubtaskStatusUpdated);

      socket.emit('join_task', selectedTask.id);

      return () => {
        socket.off('terminal_output', handleTerminalOutput);
        socket.off('task_title_updated', handleTaskTitleUpdated);
        socket.off('project_renamed', handleProjectRenamed);
        socket.off('project_bootstrap', handleProjectBootstrap);
        socket.off('chat_message', handleChatMessage);
        socket.off('chat_message_part', handleChatMessagePart);
        socket.off('chat_message_complete', handleChatMessageComplete);
        socket.off('chat_message_updated', handleChatMessageUpdated);
        socket.off('chat_message_status_changed', handleChatMessageStatusChanged);
        socket.off('task_status_changed', handleTaskStatusChanged);
        socket.off('global_task_status_changed', handleGlobalTaskStatusChanged);
        socket.off('command_started', handleCommandStarted);
        socket.off('task_list_updated', handleTaskListUpdated);
        socket.off('subtask_status_updated', handleSubtaskStatusUpdated);
        socket.emit('leave_task', selectedTask.id);
      };
    }
  }, [isConnected, selectedTask?.id, socket]);

  const value = {
    tasks,
    selectedTask,
    chatMessages,
    commandLogs,
    terminalOutput,
    executionStatus,
    activeTurnMode,
    isExecutionBlocked,
    loading,
    error,
    executions,
    setSelectedTask,
    fetchTasks,
    createTask,
    selectTask,
    refreshTaskFromDB,
    updateTask,
    deleteTask,
    sendChatMessage,
    listProjectPlans,
    buildFromSavedPlan,
    fetchChatHistory,
    pushOptimisticMessage: (msg: IChatMessage) => {
      setChatMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev;
        return [...prev, msg].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      });
    },
    removeOptimisticMessage: (id: string) => {
      setChatMessages(prev => prev.filter(m => m.id !== id));
    },
    executeCommand,
    cancelExecution,
    fetchCommandLogs,
    fetchTerminalOutput,
    fetchExecutions,
    clearError,
    retryTask,
    setExecutionStatus,
    setActiveTurnMode,
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};
